"use client";

import { useState } from "react";
import Link from "next/link";

// ----- Color theme presets -----
const colorThemes = {
  orange: {
    hoverBg: "hover:bg-orange-50",
    focusRing: "focus-visible:ring-orange-500",
    iconText: "text-orange-600",
    iconOpenBg: "bg-orange-100",
    statBg: "bg-orange-50",
    statValue: "text-orange-700",
    statLabel: "text-orange-500",
    ctaBg: "bg-orange-50",
    primaryBtn: "bg-orange-500 hover:bg-orange-600",
    secondaryText: "text-orange-600",
    secondaryBorder: "border-orange-200",
    secondaryHoverBg: "hover:bg-orange-50",
    emailLink: "text-orange-600",
    tableHeaderBg: "bg-orange-50",
    tableHeaderText: "text-orange-800",
  },
  indigo: {
    hoverBg: "hover:bg-indigo-50",
    focusRing: "focus-visible:ring-indigo-500",
    iconText: "text-indigo-600",
    iconOpenBg: "bg-indigo-100",
    statBg: "bg-indigo-50",
    statValue: "text-indigo-700",
    statLabel: "text-indigo-500",
    ctaBg: "bg-indigo-50",
    primaryBtn: "bg-indigo-600 hover:bg-indigo-700",
    secondaryText: "text-indigo-600",
    secondaryBorder: "border-indigo-200",
    secondaryHoverBg: "hover:bg-indigo-50",
    emailLink: "text-indigo-600",
    tableHeaderBg: "bg-indigo-50",
    tableHeaderText: "text-indigo-800",
  },
} as const;

export type FAQColorTheme = keyof typeof colorThemes;

export type FAQItem = {
  id: number;
  question: string;
  answer: React.ReactNode;
};

export type FAQStatCard = {
  value: string;
  label: string;
};

export type FAQCTAConfig = {
  heading: string;
  description: string;
  primaryLabel: string;
  primaryHref: string;
  secondaryLabel: string;
  secondaryHref: string;
};

// ----- Accordion item -----
function AccordionItem({
  question,
  answer,
  isOpen,
  onToggle,
  theme,
}: {
  question: string;
  answer: React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
  theme: (typeof colorThemes)[FAQColorTheme];
}) {
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        className={`w-full flex items-center justify-between px-5 py-4 text-left bg-white ${theme.hoverBg} transition-colors focus:outline-none ${theme.focusRing}`}
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        <span className="font-semibold text-gray-900 pr-4">{question}</span>
        <span
          className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${theme.iconText} transition-transform duration-200 ${
            isOpen ? `rotate-180 ${theme.iconOpenBg}` : "bg-gray-100"
          }`}
        >
          <svg
            viewBox="0 0 20 20"
            fill="currentColor"
            className="w-4 h-4"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </span>
      </button>
      {isOpen && (
        <div className="px-5 py-4 border-t border-gray-100 bg-gray-50">
          <div className="text-sm text-gray-600 leading-relaxed">{answer}</div>
        </div>
      )}
    </div>
  );
}

// ----- Main component -----
export function FAQAccordion({
  colorTheme,
  faqItems,
  statCards,
  cta,
}: {
  colorTheme: FAQColorTheme;
  faqItems: FAQItem[];
  statCards: FAQStatCard[];
  cta: FAQCTAConfig;
}) {
  const [openId, setOpenId] = useState<number | null>(null);
  const theme = colorThemes[colorTheme];

  function toggle(id: number) {
    setOpenId((prev) => (prev === id ? null : id));
  }

  return (
    <>
      {/* Stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-12">
        {statCards.map(({ value, label }) => (
          <div key={label} className={`${theme.statBg} rounded-xl p-4 text-center`}>
            <div className={`text-xl font-bold ${theme.statValue}`}>{value}</div>
            <div className={`text-xs ${theme.statLabel} mt-1`}>{label}</div>
          </div>
        ))}
      </div>

      {/* Accordion */}
      <div className="space-y-3">
        {faqItems.map((item) => (
          <AccordionItem
            key={item.id}
            question={item.question}
            answer={item.answer}
            isOpen={openId === item.id}
            onToggle={() => toggle(item.id)}
            theme={theme}
          />
        ))}
      </div>

      {/* Bottom CTA */}
      <div className={`mt-14 ${theme.ctaBg} rounded-2xl p-8 text-center`}>
        <h2 className="text-xl font-bold text-gray-900 mb-2">{cta.heading}</h2>
        <p className="text-gray-500 mb-5 text-sm">{cta.description}</p>
        <div className="flex items-center justify-center gap-4 flex-wrap">
          <Link
            href={cta.primaryHref}
            className={`inline-block ${theme.primaryBtn} text-white font-semibold px-6 py-3 rounded-xl transition-colors`}
          >
            {cta.primaryLabel}
          </Link>
          <Link
            href={cta.secondaryHref}
            className={`inline-block ${theme.secondaryText} font-semibold px-6 py-3 rounded-xl border ${theme.secondaryBorder} ${theme.secondaryHoverBg} transition-colors`}
          >
            {cta.secondaryLabel}
          </Link>
        </div>
      </div>

      <p className="text-center text-sm text-gray-400 mt-8">
        Still have questions?{" "}
        <a
          href="/feedback"
          className={`${theme.emailLink} hover:underline`}
        >
          Send us a message
        </a>
      </p>
    </>
  );
}

// Re-export the color themes map so consumers can use it for table headers etc.
export { colorThemes };
